import React, { useCallback, useRef, useMemo } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

import { useSelector } from "react-redux";

import { CardForm, PaymentSystem } from "@web-solutions/react-billing";

import Analytics from "@web-solutions/module-analytics";

import { remoteConfigSelector } from "core/store/remote-config/selectors";
import { selectPaymentSystem } from "core/store/billing/selectors";
import { useButtonTitleByConfig } from "core/utils/button-configurator";
import { ButtonPlacementType } from "core/payment/constants";

import "./style.scss";
import getLocale from "core/localization/get-locale";

const PaymentForm = ({
  product,
  onSubmit,
  onSuccess,
  onError,
  onApplePayClick,
  onApplePaySubmit,
  onGooglePayClick,
  onGooglePaySubmit,
  onUserInfoChange,
  options,
  formClassName,
  fieldClassName,
  inputStyles,
  labelStyles,
  notEmptyLabelStyles,
  isSimpleView,
  color,
  onSaveTokenOneTime,
  subscriptionPageType = 'usual',
}) => {
  const startEnterCardForm = useRef(false);
  const completedEnterCardForm = useRef(false);

  const paymentSystem = useSelector(selectPaymentSystem);
  const { additionalFields, buttonsConfigs, paymentPopup, collectEmailAfterPayment } =
    useSelector(remoteConfigSelector);
  const shippingAddressRef = useRef();
  const buttonConfig = buttonsConfigs[ButtonPlacementType.PAYMENT];
  const locale = getLocale();
  const buttonTitle = product?.buttonTitleModal?.[locale] || 'Start Now';
  const buttonClassNames = classNames("form-button", "form-button-title");

  const additionalField = useMemo(() => {
    // Deferred email: the address is collected on `email-after-payment`, and the
    // flow supplies its own (anonymous Stripe customer / synthetic truegate
    // address) when charging — so asking for it in the card form is both
    // pointless and a conversion cost.
    const { email: _emailField, ...fieldsWithoutEmail } = additionalFields || {};
    const baseFields = collectEmailAfterPayment ? fieldsWithoutEmail : additionalFields;

    if (paymentSystem !== PaymentSystem.STRIPE) {
      return baseFields;
    }

    return {
      ...baseFields,
      first_name: {
        label: '',
        placeholder: paymentPopup?.cardElementRecurly?.firstName?.[locale] || 'First Name',
        type: 'text',
      },
      last_name: {
        label: '',
        placeholder: paymentPopup?.cardElementRecurly?.lastName?.[locale] || 'Last Name',
        type: 'text',
      },
    };
  }, [additionalFields, collectEmailAfterPayment, locale, paymentPopup, paymentSystem]);

  const handleButtonSubmit = async (formData) => {
    try {
      Analytics.trackEvent("submit_pay_button", "click");
      await onSubmit(formData);
    } catch (error) {
      throw error;
    }
  };

  const handleFormReady = useCallback(() => {
    Analytics.trackEvent("card_form", "ready");
  }, []);

  const handleFormChange = useCallback(
    (e) => {
      // TODO refactor target check
      if (!e.target || paymentSystem === PaymentSystem.SOLIDGATE) {
        if (!e.error) {
          if (!startEnterCardForm.current && !e.empty) {
            startEnterCardForm.current = true;
            Analytics.trackEvent("card_form_enter", "start");
          } else if (
            !completedEnterCardForm.current &&
            (e.complete || e.valid)
          ) {
            completedEnterCardForm.current = true;
            Analytics.trackEvent("card_form_enter", "completed", {
              brand: e.brand,
            });
          }
        }
      }
    },
    [paymentSystem]
  );

  const validateChildren = () => {
    return shippingAddressRef?.current?.validateFields();
  };

  return (
    <CardForm
      isSimpleView={isSimpleView}
      product={product}
      color={color}
      onReady={handleFormReady}
      onChange={handleFormChange}
      onSubmit={handleButtonSubmit}
      onSuccess={onSuccess}
      onError={onError}
      onApplePayClick={onApplePayClick}
      onApplePaySubmit={onApplePaySubmit}
      onGooglePayClick={onGooglePayClick}
      onGooglePaySubmit={onGooglePaySubmit}
      onUserInfoChange={onUserInfoChange}
      formClassName={formClassName}
      fieldClassName={fieldClassName}
      buttonClassName={buttonClassNames}
      buttonTitle={buttonTitle}
      additionalFields={additionalField}
      options={options}
      inputStyles={inputStyles}
      labelStyles={labelStyles}
      notEmptyLabelStyles={notEmptyLabelStyles}
      onValidateChildren={validateChildren}
      onSaveTokenOneTime={onSaveTokenOneTime}
      subscriptionPageType={subscriptionPageType}
    ></CardForm>
  );
};

PaymentForm.propTypes = {
  product: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  onError: PropTypes.func.isRequired,
  onApplePayClick: PropTypes.func.isRequired,
  onApplePaySubmit: PropTypes.func.isRequired,
  onGooglePayClick: PropTypes.func.isRequired,
  onGooglePaySubmit: PropTypes.func.isRequired,
  onUserInfoChange: PropTypes.func.isRequired,
  options: PropTypes.object,
  labelStyles: PropTypes.object,
  inputStyles: PropTypes.object,
  notEmptyLabelStyles: PropTypes.object,
};

PaymentForm.defaultProps = {
  options: null,
  inputStyles: null,
  labelStyles: null,
  notEmptyLabelStyles: null,
};

export default PaymentForm;
